import { Component, useEffect, useRef } from 'react';
import {
  Chart, BarController, LineController, PieController, DoughnutController,
  BarElement, LineElement, PointElement, ArcElement,
  CategoryScale, LinearScale, Tooltip, Legend, Title
} from 'chart.js';

Chart.register(
  BarController, LineController, PieController, DoughnutController,
  BarElement, LineElement, PointElement, ArcElement,
  CategoryScale, LinearScale, Tooltip, Legend, Title
);

/* ---------------------------- brand palette ----------------------------
   Spec brand: navy #16132f primary, teal #45bdb0 accent, plus orange and
   magenta for further series. Extended with the portal's own accents so a
   chart with many series doesn't run out of distinct colours.
   --------------------------------------------------------------------- */
const SERIES = ['#45bdb0', '#ff7a17', '#fd49be', '#16132f', '#7a5cff', '#0a7d73'];

/* ------------------------------ formatting -----------------------------
   Currency is GBP and arrives as raw numbers. The spec says to format as
   money where the column name implies it.
   --------------------------------------------------------------------- */
const MONEY_COLUMN = /(_revenue|_amount|_outstanding|_value|_cost|_total|_price|_balance)$/i;

export const isMoneyColumn = (name) => MONEY_COLUMN.test(String(name ?? ''));

export function formatCell(value, columnName) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value !== 'number' || Number.isNaN(value)) return String(value);

  if (isMoneyColumn(columnName)) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: 'GBP',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(value);
  }
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(value);
}

export const isNumeric = (value) => typeof value === 'number' && !Number.isNaN(value);

/* -------------------------------- table -------------------------------- */

export function DataTable({ spec }) {
  const columns = Array.isArray(spec?.columns) ? spec.columns : [];
  const rows = Array.isArray(spec?.rows) ? spec.rows : [];
  if (!columns.length) throw new Error('Table spec has no columns');

  return (
    <figure className="ai-visual">
      {spec.title && <figcaption className="ai-visual-title">{spec.title}</figcaption>}
      <div className="ai-table-wrap">
        <table className="table table-sm ai-table mb-0">
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={i} className={isMoneyColumn(c) ? 'text-end' : undefined}>
                  {humanise(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {columns.map((c, i) => {
                  const value = Array.isArray(row) ? row[i] : row?.[c];
                  return (
                    <td key={i} className={isNumeric(value) ? 'text-end ai-num' : undefined}>
                      {formatCell(value, c)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && <p className="ai-visual-empty">No rows returned.</p>}
    </figure>
  );
}

const humanise = (name) =>
  String(name ?? '').replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());

/* -------------------------------- chart -------------------------------- */

export function DataChart({ spec }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const type = spec?.type;
  const valid = ['bar', 'line', 'pie', 'doughnut'].includes(type);
  if (!valid) throw new Error(`Unsupported chart type: ${type}`);
  if (!Array.isArray(spec.labels) || !Array.isArray(spec.datasets)) {
    throw new Error('Chart spec is missing labels or datasets');
  }

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const isPie = type === 'pie' || type === 'doughnut';
    const datasets = spec.datasets.map((ds, i) => ({
      label: ds.label ?? undefined,
      data: Array.isArray(ds.data) ? ds.data : [],
      // Pie slices need a colour each; bars and lines one per series.
      backgroundColor: isPie
        ? spec.labels.map((_, j) => SERIES[j % SERIES.length])
        : SERIES[i % SERIES.length],
      borderColor: isPie ? 'transparent' : SERIES[i % SERIES.length],
      borderWidth: type === 'line' ? 2 : 0,
      tension: type === 'line' ? 0.3 : undefined,
      pointRadius: type === 'line' ? 3 : undefined
    }));

    chartRef.current = new Chart(ctx, {
      type,
      data: { labels: spec.labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 200 },
        plugins: {
          legend: {
            display: isPie || spec.datasets.length > 1,
            position: isPie ? 'right' : 'top',
            labels: { boxWidth: 12, font: { size: 11 } }
          },
          tooltip: {
            callbacks: {
              label: (item) => {
                const label = item.dataset.label ? `${item.dataset.label}: ` : '';
                return label + formatCell(item.parsed.y ?? item.parsed, item.dataset.label);
              }
            }
          }
        },
        scales: isPie ? undefined : {
          x: { grid: { display: false }, ticks: { font: { size: 11 } } },
          y: {
            beginAtZero: true,
            ticks: {
              font: { size: 11 },
              callback: (v) => new Intl.NumberFormat('en-GB', { notation: 'compact' }).format(v)
            }
          }
        }
      }
    });

    return () => { chartRef.current?.destroy(); chartRef.current = null; };
  }, [spec, type]);

  return (
    <figure className="ai-visual">
      {spec.title && <figcaption className="ai-visual-title">{spec.title}</figcaption>}
      <div className="ai-chart"><canvas ref={canvasRef} /></div>
    </figure>
  );
}

/* --------------------------- error boundary ----------------------------
   Spec requirement: a malformed spec must degrade to a message, never
   blank the page. A thrown error inside a chart or table would otherwise
   unmount the whole conversation.
   --------------------------------------------------------------------- */

export class VisualBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.warn('AI visual failed to render', error);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="ai-visual ai-visual-failed">
          <i className="bi bi-exclamation-triangle" /> Couldn’t render this visual.
        </div>
      );
    }
    return this.props.children;
  }
}

export function Visual({ visual }) {
  return (
    <VisualBoundary>
      {visual.kind === 'chart'
        ? <DataChart spec={visual.spec} />
        : <DataTable spec={visual.spec} />}
    </VisualBoundary>
  );
}
